import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../../../components/itemsPerPage.component';
import { I18nTranslator } from '../../../../i18n/translate';
import { ARColzaResultType } from '../../../../models/agrorendement-colza-lists.model';
import { ARColzaCampaign, ARColzaOrderItem } from '../../../../models/agrorendement-colza.model';
import { BreakOnUnderscorePipe } from '../../../../pipes/breakOnUnderscore.pipe';
import { CampaignYearPipe } from '../../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../../../pipes/hectareConverter.pipe';
import { ExtractFarmNameFromOrderItemsPipe, ExtractParcelNameFromOrderItemsPipe } from '../../../../pipes/results.pipe';
import { ToLabelPipe } from '../../../../pipes/toLabel.pipe';
import { ARColzaListType, ARColzaListsService } from '../../../../services/agrorendement-colza-lists.service';
import { ARColzaService } from '../../../../services/agrorendement-colza.service';
import { FarmService } from '../../../../services/farm.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { UserService } from '../../../../services/user.service';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { sortingString, stob, toNull, toNumber } from '../../../../utils/utils';
import { ARResultsComponent } from '../common/results.component';
import { ARColzaUtils } from './colza.utils';
import { TranslatePipe, TranslateDatePipe } from '../../../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { MultiSelectComponent } from '../../../../components/multi-select/multi-select.component';

@Component({
  standalone: true,
  selector: 'ar-colza-results-cmp',
  templateUrl: '../common/results.component.html',
  styleUrls: ['../common/results.component.scss'],
  imports: [
    MultiSelectComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    FormsModule,
    Tooltip,
    TooltipContent,
    ItemsPerPageComponent,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    BreakOnUnderscorePipe,
    ExtractParcelNameFromOrderItemsPipe,
    ExtractFarmNameFromOrderItemsPipe,
    TranslatePipe,
    TranslateDatePipe,
    PaginationModule,
    TooltipModule,
  ],
  providers: [ARColzaService, FarmService, OrganisationService, UserService],
})
export class ARColzaResultsComponent extends ARResultsComponent<ARColzaOrderItem, ARColzaCampaign, ARColzaResultType> {
  @ViewChild('termPrefixFilterParcel') public termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterExploitation') public termPrefixFilterExploitation: ElementRef;
  @ViewChild('termFarmFilter') termFarmFilter: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termOrganisationFilter') termOrganisationFilter: ElementRef;
  @ViewChild('termValidationStatusFilter') termValidationStatusFilter: ElementRef;
  @ViewChild('termAvailabilityStatusFilter') termAvailabilityStatusFilter: ElementRef;
  @ViewChild('termMarkedForNotificationStatusFilter') termMarkedForNotificationStatusFilter: ElementRef;
  @ViewChild('termNotificationStatusFilter') termNotificationStatusFilter: ElementRef;
  @ViewChild('termNotificationAdvisorsStatusFilter') termNotificationAdvisorsStatusFilter: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;

  @ViewChildren('resultCheckbox') resultCheckboxes: any;
  @ViewChild('checkboxAll') checkboxAll: any;
  isAdmin: boolean;

  constructor(
    protected service: ARColzaService,
    protected listService: ARColzaListsService,
    protected organisationService: OrganisationService,
    protected farmService: FarmService,
    protected i18nTranslator: I18nTranslator,
    protected router: Router,
    protected route: ActivatedRoute,
    protected modal: ModalService,
    protected ref: ChangeDetectorRef,
    protected userService: UserService,
  ) {
    super();
    this.specyString = this.i18nTranslator.translate('ar.colza.section');
    this.specyUrlPart = ['azote', 'colza'];
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  isCampaignCurrentlyOpen(campaign: ARColzaCampaign): boolean {
    return ARColzaUtils.isCampaignCurrentlyOpen(campaign);
  }

  getResultTypes(): Observable<Map<any, ARColzaResultType[]>> {
    const listTypes = [ARColzaListType.COLZA_RESULT_TYPES];
    const obs1 = this.listService.getListTypes(ARColzaListType, ...listTypes).pipe(share());
    obs1.subscribe((lists) => {
      this.resultTypes = <ARColzaResultType[]>(
        lists.get(ARColzaListType.COLZA_RESULT_TYPES).sort(sortingString('label'))
      );
      const rawResultTypes: any = this.resultTypes;

      this.resultTypes = this.resultTypes.filter(function (item) {
        return item.visible;
      });

      this.resultTypesDict = new Map<string, ARColzaResultType>();
      for (const resultType of rawResultTypes) {
        this.resultTypesDict.set(resultType.code, resultType);
      }
    });
    return <Observable<Map<any, ARColzaResultType[]>>>obs1;
  }

  isGenerateConsole = true;
  navigateToGenerateConsole(): any {
    let notificationAdvisorsStatus: boolean = null;
    if (this.notificationAdvisorsStatusFilter) {
      notificationAdvisorsStatus = stob('' + this.notificationAdvisorsStatusFilter);
    }
    const filterToSave = JSON.stringify({
      prefixFilterParcel: this.prefixFilterParcel,
      prefixFilterExploitation: this.prefixFilterExploitation,
      prefixFilterIdParcel: toNumber(this.prefixFilterIdParcel),
      campaignFilter: toNull(this.campaignFilter),
      organisationFilter: toNull(this.organisationFilter),
      resultTypeFilter: toNull(this.resultTypeFilter),
      validationStatusFilter: stob('' + this.validationStatusFilter),
      availabilityStatusFilter: stob('' + this.availabilityStatusFilter),
      notificationStatusFilter: stob('' + this.notificationStatusFilter),
      notificationAdvisorsStatusFilter: notificationAdvisorsStatus,
      advisorFilter: toNull(this.advisorFilter),
    });
    sessionStorage.setItem(this.specyUrlPart[1] + 'Filter', filterToSave);
    this.router.navigate(['/results', 'azote', 'colza', 'genconsole']);
  }
}
